/**
 * BuildingExternalContactsPage
 * Lists a building's external contacts and (for admins) supports linking an
 * existing registry contact or creating a new one, plus remove and edit-note.
 * Mirrors ProjectExternalContactsSection; the read path loads users/userList
 * directly (via useBuildingExternalContacts) so the list renders for non-admins.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { Button, Badge, Form } from 'react-bootstrap';
import { Plus, User, Mail, Phone, Building, ChevronDown, ChevronRight, Trash2, Edit2, Info } from 'lucide-react';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuildingKey } from '../../Helpers/utils';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useUsersData } from '../../Helpers/UsersContext';
import { useUserNames } from '../../Helpers/useUserNames';
import { useBuildingExternalContacts } from '../../hooks/useBuildingExternalContacts';
import { BuildingExternalContactWithUser } from '../../api/buildingExternalContactsApi';
import { ConfirmDeleteModal } from '../../Components/ConfirmDeleteModal';
import { MetadataInfoButton } from '../../Components/MetadataInfoButton';
import { MAX_BUILDING_NOTE_LENGTH } from '../../api/buildingExternalContactsApi';
import { AddBuildingExternalContactModal } from './components/AddBuildingExternalContactModal';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { formatDateTimeForDisplay } from '../../Helpers/DateUtils';
import './BuildingExternalContactsPage.css';

interface ContactItemProps {
    contact: BuildingExternalContactWithUser;
    isAdmin: boolean;
    onRemove: () => void;
    onEditNote: (note: string) => Promise<void>;
    submitting: boolean;
    addedByName?: string;
}

const ContactItem: React.FC<ContactItemProps> = ({
    contact,
    isAdmin,
    onRemove,
    onEditNote,
    submitting,
    addedByName,
}) => {
    const { user } = contact;
    const [expanded, setExpanded] = useState(false);
    const [editingNote, setEditingNote] = useState(false);
    const [noteValue, setNoteValue] = useState(contact.buildingNote || '');
    // The info button only opens on hover, so phones show who added the contact
    // in the expanded details instead.
    const isSmallScreen = useIsSmallScreen();

    const handleSaveNote = async () => {
        await onEditNote(noteValue);
        setEditingNote(false);
    };

    const handleCancelNote = () => {
        setNoteValue(contact.buildingNote || '');
        setEditingNote(false);
    };

    return (
        <div className="std-box" style={{ marginBottom: '8px' }}>
            <div className="external-contact-row" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <User size={18} />
                <div className="external-contact-title">
                    <strong>{user.name}</strong>
                    {user.role && <Badge bg="secondary">{user.role}</Badge>}
                </div>
                {contact.addedBy && !isSmallScreen && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <MetadataInfoButton
                            entries={[
                                {
                                    label: 'Tillagd',
                                    date: contact.addedAt,
                                    userName: addedByName || 'Okänd användare',
                                },
                            ]}
                        />
                    </div>
                )}
                {isAdmin && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        disabled={submitting}
                    >
                        <Trash2 size={14} />
                    </Button>
                )}
            </div>

            {expanded && (
                <div className="external-contact-details">
                    {user.company && (
                        <div className="external-contact-line" style={{ color: '#666' }}>
                            <Building size={14} />
                            <span>{user.company}</span>
                        </div>
                    )}
                    {user.email && (
                        <div className="external-contact-line">
                            <Mail size={14} />
                            <a href={`mailto:${user.email}`}>{user.email}</a>
                        </div>
                    )}
                    {user.phone && (
                        <div className="external-contact-line">
                            <Phone size={14} />
                            <a href={`tel:${user.phone}`}>{user.phone}</a>
                        </div>
                    )}
                    {contact.addedBy && isSmallScreen && (
                        <div className="external-contact-line" style={{ color: '#666', fontSize: '14px' }}>
                            <Info size={14} />
                            <span>
                                Tillagd {formatDateTimeForDisplay(contact.addedAt)} av {addedByName || 'Okänd användare'}
                            </span>
                        </div>
                    )}

                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '14px', color: '#666' }}>Byggnadsanteckning</strong>
                            {isAdmin && !editingNote && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="external-contact-note-edit"
                                    onClick={() => setEditingNote(true)}
                                    disabled={submitting}
                                    style={{ padding: 0 }}
                                    aria-label="Redigera anteckning"
                                >
                                    <Edit2 size={12} />
                                </Button>
                            )}
                        </div>
                        {editingNote ? (
                            <div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={MAX_BUILDING_NOTE_LENGTH}
                                    value={noteValue}
                                    onChange={(e) => setNoteValue(e.target.value)}
                                    placeholder="Lägg till en anteckning..."
                                    autoFocus
                                />
                                <small className={`d-block mt-1 text-end ${noteValue.length >= MAX_BUILDING_NOTE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                    {noteValue.length} / {MAX_BUILDING_NOTE_LENGTH}
                                </small>
                                <div className="external-contact-note-actions" style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                    <Button variant="primary" size="sm" onClick={handleSaveNote} disabled={submitting}>
                                        Spara
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={handleCancelNote} disabled={submitting}>
                                        Avbryt
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <p style={{ margin: 0, color: contact.buildingNote ? '#333' : '#999', fontStyle: contact.buildingNote ? 'normal' : 'italic' }}>
                                {contact.buildingNote || 'Ingen anteckning'}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const BuildingExternalContactsPage: React.FC = () => {
    const buildingKey = useBuildingKey() || '';
    const { currentBuilding, loadingBuilding } = useBuilding();
    const { users } = useUsersData();
    const { userNames, loadUserNames } = useUserNames();

    const {
        contacts,
        loading,
        error,
        submitting,
        addContact,
        removeContact,
        updateNote,
    } = useBuildingExternalContacts(buildingKey);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const currentUserId = getAuth().currentUser?.uid;
    const isAdmin = users?.find((u) => u.key === currentUserId)?.admin ?? false;

    const buildingName = currentBuilding?.summary?.name || buildingKey || 'Byggnad';
    useDocumentTitle(`Externa kontakter - ${buildingName}`);

    const linkedContactIds = useMemo(() => contacts.map((c) => c.externalUserId), [contacts]);

    const addedByUids = useMemo(
        () => contacts.map((c) => c.addedBy).filter(Boolean),
        [contacts]
    );
    useEffect(() => {
        if (addedByUids.length > 0) loadUserNames(addedByUids);
    }, [addedByUids, loadUserNames]);

    const handleRemoveClick = (externalUserId: string) => {
        setPendingDeleteId(externalUserId);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (pendingDeleteId) {
            await removeContact(pendingDeleteId);
            setPendingDeleteId(null);
        }
    };

    const pendingContact = pendingDeleteId
        ? contacts.find((c) => c.externalUserId === pendingDeleteId)
        : null;

    const breadcrumbItems = [
        { text: buildingName, link: `/building/${buildingKey}` },
        { text: 'Externa kontakter' },
    ];

    if (loadingBuilding) return <PageLoader />;

    return (
        <div>
            <BreadcrumbComponent items={breadcrumbItems} />

            <div className="external-contacts-page">
            <div className="std-box">
                <div className="external-contacts-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h5 style={{ margin: 0 }}>Externa kontakter</h5>
                        {contacts.length > 0 && <Badge bg="secondary">{contacts.length}</Badge>}
                    </div>
                    {isAdmin && (
                        <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)} disabled={submitting}>
                            <Plus size={14} className="me-1" />
                            Lägg till kontakt
                        </Button>
                    )}
                </div>
                <div className="external-contacts-body">
                    {error && <p style={{ color: '#b00', marginBottom: '12px' }}>{error}</p>}
                    {loading ? (
                        <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>Laddar...</p>
                    ) : contacts.length > 0 ? (
                        <div>
                            {contacts.map((contact) => (
                                <ContactItem
                                    key={contact.externalUserId}
                                    contact={contact}
                                    isAdmin={isAdmin}
                                    onRemove={() => handleRemoveClick(contact.externalUserId)}
                                    onEditNote={(note) => updateNote(contact.externalUserId, note)}
                                    submitting={submitting}
                                    addedByName={contact.addedBy ? userNames[contact.addedBy] || undefined : undefined}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>
                            Inga externa kontakter har lagts till.
                        </p>
                    )}
                </div>
            </div>
            </div>

            {isAdmin && (
                <AddBuildingExternalContactModal
                    show={showAddModal}
                    onHide={() => setShowAddModal(false)}
                    onAdd={addContact}
                    submitting={submitting}
                    linkedContactIds={linkedContactIds}
                />
            )}

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setPendingDeleteId(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Ta bort kontakt"
                message={
                    pendingContact
                        ? `Vill du ta bort ${pendingContact.user.name} från byggnadens kontaktlista? Kontakten finns kvar i det centrala registret.`
                        : 'Vill du ta bort denna kontakt från byggnaden?'
                }
            />
        </div>
    );
};
